import { useEffect, useState } from 'react';
import QRCodeLib from 'qrcode';

interface QRCodeProps {
  /** Payload to encode. Treated opaquely — callers decide whether to
   *  encode a raw code, a URL, or a JSON string. */
  value: string;
  /** Rendered pixel size of the QR (square). */
  size?: number;
  /** Accessible label / `alt` for the underlying image. */
  alt?: string;
  className?: string;
}

/**
 * Renders a QR code as an SVG data URL using the `qrcode` package.
 *
 * Pure client-side rendering — no API endpoint needed. The same
 * payload always produces the same image, so callers can right-click
 * → save the image (and the print view embeds the same data URLs).
 */
export function QRCode({ value, size = 192, alt = 'QR code', className }: QRCodeProps) {
  const [dataUrl, setDataUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    QRCodeLib.toDataURL(value, {
      width: size,
      margin: 1,
      errorCorrectionLevel: 'M',
    })
      .then((url) => {
        if (!cancelled) setDataUrl(url);
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message || 'Failed to generate QR code');
      });
    return () => {
      cancelled = true;
    };
  }, [value, size]);

  if (error) {
    return (
      <div
        className={`flex items-center justify-center rounded border border-destructive/30 bg-destructive/5 p-2 text-xs text-destructive ${className ?? ''}`}
        style={{ width: size, height: size }}>
        {error}
      </div>
    );
  }

  if (!dataUrl) {
    return (
      <div
        className={`animate-pulse rounded bg-muted ${className ?? ''}`}
        style={{ width: size, height: size }}
      />
    );
  }

  return (
    <img
      src={dataUrl}
      width={size}
      height={size}
      alt={alt}
      className={`rounded bg-white p-2 ${className ?? ''}`}
    />
  );
}
